import React, { useState, useEffect, useRef } from "react";
import { X, Trash2, Save, Paperclip, Bell, Check, RotateCcw, FileText, File as FileIcon, Zap, Edit3, Flag, Scissors } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { processFile, todayStr, getOccurrencesOnIso, effectiveOverrides, formatDate, proposeSplit, planOccurrenceEdit, extractUrls } from "@/utils";

/** Which beat of a recurring series is being edited: the row the user tapped, else today's
 *  beat, else the next upcoming one. Never the series anchor — that would look like a
 *  past-dated task and, if saved as "just this occurrence", would move a non-existent beat. */
const resolveOccurrenceIso = (task) => {
    if (!task || task.type !== "recurring" || !task.interval) return task?.date || "";
    if (task._occKey) return task._occKey;            // identity of the row that was opened (source date)
    if (task._occurrenceDate) {
        // Legacy DISPLAY context (Weekly/Monthly grids) — never accepted as a source key blindly.
        // Resolve what actually lands there; exactly one obligation → its SOURCE key. Several →
        // refuse to guess (series scope only until the caller picks one).
        const occs = getOccurrencesOnIso(task, task._occurrenceDate);
        if (occs.length === 1) return occs[0].key;
        if (occs.length > 1) return "";
    }
    // Fallback scan from today: return the obligation's SOURCE key, never the display date —
    // a beat moved Sep 1 → Sep 12 found on Sep 12 must resolve to "…-09-01".
    const d = new Date();
    for (let i = 0; i < 400; i++) {
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const occs = getOccurrencesOnIso(task, iso);
        if (occs.length) return occs[0].key;
        d.setDate(d.getDate() + 1);
    }
    return task.date || "";
};

/** Display date of the obligation the editor is about (where it currently sits on the calendar). */
const resolveDisplayIso = (task) => {
    if (!task || task.type !== "recurring" || !task.interval) return task?.date || "";
    if (task._displayIso) return task._displayIso;
    const key = resolveOccurrenceIso(task);
    if (!key) return task._occurrenceDate || "";
    const ov = effectiveOverrides(task).find((o) => o.from === key);
    return ov?.to || key;
};
import { api } from "@/api";
import { logDiag, stateSummary } from "@/lib/diagnostics";
import { toast } from "sonner";
import { MediaLightbox } from "@/components/MediaLightbox";
import { OccurrenceHistoryList } from "@/components/OccurrenceHistoryList";
import { DatePicker, TimePicker } from "@/components/DateTimePicker";

// Reminder unit → minutes multiplier
const UNIT_MIN = { min: 1, hr: 60, day: 1440, wk: 10080 };
const REMINDER_PRESETS = [
    { l: "1 day", v: 1440 },
    { l: "3 days", v: 4320 },
    { l: "1 week", v: 10080 },
    { l: "2 weeks", v: 20160 },
];
const NAG_PRESETS = [
    { l: "1 min", v: 1 },
    { l: "5 min", v: 5 },
    { l: "15 min", v: 15 },
];

/** Format an arbitrary minute count into a compact label ("30 min", "2 hr", "3 day", "2 wk"). */
const formatMinutes = (m) => {
    if (m % 10080 === 0) return `${m / 10080} wk`;
    if (m % 1440 === 0) return `${m / 1440} day`;
    if (m % 60 === 0) return `${m / 60} hr`;
    return `${m} min`;
};

export const TaskEditor = ({ task, open, onOpenChange, onSave, onDelete, onComplete, onPostpone, onTaskUpdated, refreshAttachments, attachments }) => {
    const [text, setText] = useState("");
    const [description, setDescription] = useState("");
    const [splitPreview, setSplitPreview] = useState(null);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [type, setType] = useState("todo");
    const [interval, setIntervalVal] = useState(7);
    const [remindersMin, setRemindersMin] = useState([]);
    const [nagEveryMin, setNagEveryMin] = useState(1);
    const [priority, setPriority] = useState("med");
    const [customVal, setCustomVal] = useState("");
    const [customUnit, setCustomUnit] = useState("hr");
    const [preview, setPreview] = useState(null);
    const [editingAtts, setEditingAtts] = useState(false);
    // Two-step delete confirmation for recurring tasks — pressing "delete" once flips this on,
    // exposing a "Delete series" / "Cancel" strip so a stray tap can't nuke a running cadence.
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    // Scope for recurring date/time edits — "occurrence" (default, safer: only THIS beat moves)
    // or "series" (re-anchors every future occurrence).
    const [scope, setScope] = useState("occurrence");
    // Track the initial date/time this editor OPENED with (which may be the anchor OR — for
    // recurring tasks opened from a specific weekday row — the clicked occurrence's date).
    // dateChanged/timeChanged compare against these, not against the task's stored anchor,
    // so opening a recurring row from Aug 28 doesn't immediately look like an "edit".
    const initialDateRef = useRef("");
    const initialTimeRef = useRef("");
    const fileRef = useRef(null);

    useEffect(() => {
        if (!task) return;
        setText(task.text || "");
        setDescription(task.description || "");
        setSplitPreview(null);
        // For a recurring task opened from a SPECIFIC day row (WeeklyView / MonthlyView pass
        // `_occurrenceDate`), prefill the date field with THAT occurrence's date instead of
        // the anchor. Editing then correctly targets the beat the user tapped (Fri Aug 28)
        // rather than the anchor (Mon Aug 10) which would re-cadence the whole series.
        // Display the day the beat is SHOWN on (a moved beat shows its destination day); its
        // identity for save/complete stays the source date via resolveOccurrenceIso().
        const initialDate = task.type === "recurring" ? resolveDisplayIso(task) : (task.date || "");
        setDate(initialDate);
        setTime(task.time || "");
        initialDateRef.current = initialDate;
        initialTimeRef.current = task.time || "";
        setType(task.type || "todo");
        setIntervalVal(task.interval || 7);
        // Merge legacy `reminders` (days) into the new minutes-based list
        const legacyMinutes = (task.reminders || []).map((d) => (typeof d === "number" ? d * 1440 : 0)).filter(Boolean);
        setRemindersMin(Array.from(new Set([...(task.reminders_min || []), ...legacyMinutes])).sort((a, b) => a - b));
        setNagEveryMin(task.nag_every_min || 1);
        setPriority(task.priority || "med");
        setPreview(null);
        setEditingAtts(false);
        setConfirmingDelete(false);
        setScope("occurrence");
    }, [task]);

    useEffect(() => {
        if (!open) setPreview(null);
    }, [open]);

    if (!task) return null;

    const atts = (attachments || []).filter((a) => a.parent_id === task.id);

    const deleteAttachment = async (a, e) => {
        e?.stopPropagation();
        try {
            await api.deleteAttachment(a.id);
            toast.success("Attachment removed");
            refreshAttachments?.();
        } catch (err) {
            console.error(err);
            toast.error(err?.message || "Delete failed");
        }
    };

    // Helpers: has the user edited the date or the time? Compare against the value the
    // editor OPENED with (which is the clicked occurrence for recurring rows), not against
    // the task's stored anchor — otherwise opening Fri Aug 28's recurring beat immediately
    // looks like an "Aug 10 → Aug 28" edit and triggers the scope prompt on save.
    const isRecurringSeries = task?.type === "recurring" && !!task?.interval;
    const selectedSourceIso = isRecurringSeries ? resolveOccurrenceIso(task) : null;
    const occDone = isRecurringSeries && (task.completions || []).includes(selectedSourceIso);
    const dateChanged = () => (date || "") !== (initialDateRef.current || "");
    const timeChanged = () => (time || "") !== (initialTimeRef.current || "");
    // Show the "just this occurrence / entire series" choice only when it actually matters:
    // recurring task + user changed date OR time. Anything else saves normally.
    const needsScopePrompt = () =>
        task?.type === "recurring" && type === "recurring" && (dateChanged() || timeChanged());

    // Persist the current form state. Called explicitly by Done/Reopen AND automatically when
    // the dialog closes (X / Esc / click-outside / auto-close after delete). Returns true iff
    // the write actually happened — callers can chain further actions safely.
    const save = async (opts = {}) => {
        const { silent = false } = opts;
        try {
            // "Just this occurrence" — recurring task with a shifted date/time. Move only the
            // beat that falls on `task._occurrenceDate` (default = today). Non-date/time fields
            // still save to the base task in the same round-trip so text/priority edits stick.
            if (needsScopePrompt() && scope === "occurrence" && !onPostpone) {
                toast.error("Can't move a single occurrence from here — choose \"entire series\" or open the task from the list");
                return false;
            }
            if (needsScopePrompt() && scope === "occurrence" && onPostpone) {
                if (!resolveOccurrenceIso(task)) {
                    // Several obligations land on the opened day and none was picked — refuse to guess.
                    toast.error("Several occurrences land on that day — open it from Today/History to edit one");
                    return false;
                }
                // Identity = SOURCE key. A time-only edit keeps the beat on its CURRENT display day
                // (Sep1 moved to Sep15, change 1pm→2pm ⇒ Sep1→Sep15 @14:00 — never Sep1→Sep1).
                const { fromIso, toIso, toTime } = planOccurrenceEdit(task, {
                    sourceIso: resolveOccurrenceIso(task), displayIso: resolveDisplayIso(task),
                    date, dateChanged: dateChanged(), time,
                });
                const ok = await onPostpone(task, fromIso, toIso, toTime, "editor");
                if (!ok) return false;
                // Also flush non-date/time edits to base task if any
                const baseDirty =
                    (text || "") !== (task.text || "") ||
                    (description || "").trim() !== (task.description || "").trim() ||
                    (interval || 7) !== (task.interval || 7) ||
                    (nagEveryMin || 1) !== (task.nag_every_min || 1) ||
                    (priority || "med") !== (task.priority || "med") ||
                    JSON.stringify([...(remindersMin || [])].sort()) !==
                        JSON.stringify([...(task.reminders_min || [])].sort());
                if (baseDirty) {
                    await onSave(task.id, {
                        text,
                        description: description.trim() || null,
                        // preserve the original anchor date/time on the base task
                        date: task.date || null,
                        time: task.time || null,
                        type,
                        interval: type === "recurring" ? interval : null,
                        reminders: [],
                        reminders_min: remindersMin,
                        nag_every_min: nagEveryMin || 1,
                        priority: priority || "med",
                        fired_reminders_min: [],
                    });
                }
                if (!silent) toast.success("This occurrence moved");
                return true;
            }
            // Recurring series save: if the user didn't change the displayed date (which for a
            // recurring row opened from a specific weekday is the OCCURRENCE date, not the
            // anchor), keep the anchor date on disk untouched — otherwise we'd silently
            // re-anchor the whole cadence to the clicked-day just because the user tapped Save.
            const savedDate = task?.type === "recurring" && !dateChanged() ? (task.date || null) : (date || null);
            const savedTime = task?.type === "recurring" && !timeChanged() ? (task.time || null) : (time || null);
            await onSave(task.id, {
                text,
                description: description.trim() || null,
                date: savedDate,
                time: savedTime,
                type,
                interval: type === "recurring" ? interval : null,
                reminders: [],  // clear legacy — everything lives in reminders_min now
                reminders_min: remindersMin,
                nag_every_min: nagEveryMin || 1,
                priority: priority || "med",
                // Reset fired reminders so newly set advance-reminders can fire
                fired_reminders_min: [],
            });
            if (!silent) toast.success("Task saved");
            return true;
        } catch (err) {
            console.error("save failed", err);
            if (!silent) {
                const status = err?.response?.status;
                toast.error(status === 401 ? "Session expired — sign in again to save" : (err?.response?.data?.detail || err?.message || "Save failed"));
            }
            return false;
        }
    };

    // True iff the form has diverged from the task on disk — so we only autosave when there
    // is actually something to write. Avoids toasting on a plain "look and close" open.
    // Uses dateChanged()/timeChanged() so prefilling with an occurrence date doesn't look dirty.
    const isDirty = () => {
        if (!task) return false;
        if ((text || "") !== (task.text || "")) return true;
        if ((description || "").trim() !== (task.description || "").trim()) return true;
        if (dateChanged()) return true;
        if (timeChanged()) return true;
        if ((type || "todo") !== (task.type || "todo")) return true;
        if (type === "recurring" && (interval || 7) !== (task.interval || 7)) return true;
        const existingR = new Set(task.reminders_min || []);
        const currentR = new Set(remindersMin || []);
        if (existingR.size !== currentR.size || [...currentR].some((v) => !existingR.has(v))) return true;
        if ((nagEveryMin || 1) !== (task.nag_every_min || 1)) return true;
        if ((priority || "med") !== (task.priority || "med")) return true;
        return false;
    };

    // Autosave on close — dialog dismissal never loses edits.
    const handleOpenChange = async (nextOpen) => {
        if (!nextOpen && isDirty()) {
            await save({ silent: true });
        }
        onOpenChange(nextOpen);
    };

    const uploadFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const loadingId = toast.loading(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}…`);
        let ok = 0;
        let fail = 0;
        for (const f of files) {
            try {
                const p = await processFile(f);
                await api.createAttachment({ parent_id: task.id, name: p.name, type: p.type, data: p.data });
                ok += 1;
                refreshAttachments?.();
            } catch (err) {
                console.error("attachment upload failed:", err);
                fail += 1;
                toast.error(err?.message || `Couldn't save ${f.name}`);
            }
        }
        toast.dismiss(loadingId);
        if (ok > 0) toast.success(`Saved ${ok} attachment${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
        if (fileRef.current) fileRef.current.value = "";
    };

    const toggleReminder = (mins) => {
        setRemindersMin((r) => (r.includes(mins) ? r.filter((x) => x !== mins) : [...r, mins].sort((a, b) => a - b)));
    };

    const addCustomReminder = () => {
        const n = parseInt(customVal, 10);
        if (!Number.isFinite(n) || n <= 0) {
            toast.error("Enter a positive number");
            return;
        }
        const mins = n * (UNIT_MIN[customUnit] || 60);
        setRemindersMin((r) => (r.includes(mins) ? r : [...r, mins].sort((a, b) => a - b)));
        setCustomVal("");
    };

    return (
        <Dialog
            open={open}
            modal={!preview}
            onOpenChange={(next) => {
                // Refuse to close the editor while the media lightbox is open —
                // the outside-click / Escape belongs to the lightbox layer above.
                if (!next && preview) return;
                handleOpenChange(next);
            }}
        >
            <DialogContent
                className="bg-meye-card border border-meye-border max-w-lg text-zinc-100 p-0 gap-0 max-h-[90vh] flex flex-col overflow-hidden"
                data-testid="task-editor-dialog"
            >
                <DialogTitle className="sr-only">Edit Task</DialogTitle>
                <div className="flex items-center justify-between px-4 py-3 border-b border-meye-border flex-shrink-0">
                    <span className="font-brand uppercase tracking-widest text-sm text-meye-cyan">edit task</span>
                    <button onClick={() => handleOpenChange(false)} className="text-zinc-500 hover:text-white">
                        <X size={16} />
                    </button>
                </div>

                {/* Scrollable body — header above and the action footer below stay pinned so
                    Save/Apply is reachable at any viewport height. */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4" data-testid="task-editor-body">
                    <input ref={fileRef} type="file" multiple hidden onChange={uploadFiles} />

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-[10px] uppercase tracking-widest text-zinc-500">title</label>
                            {!description.trim() && proposeSplit(text) && !splitPreview && (
                                <button
                                    type="button"
                                    onClick={() => setSplitPreview(proposeSplit(text))}
                                    className="flex items-center gap-1 text-[10px] font-brand font-bold uppercase tracking-widest text-meye-cyan hover:text-white"
                                    data-testid="task-edit-split-btn"
                                >
                                    <Scissors size={11} /> split into title + body
                                </button>
                            )}
                        </div>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={2}
                            className="w-full bg-black/40 border border-meye-border rounded p-2.5 text-sm text-zinc-100 font-semibold focus:outline-none focus:border-meye-cyan/60"
                            data-testid="task-edit-text"
                        />
                    </div>
                    {splitPreview && (
                        <div className="rounded border border-meye-cyan/40 bg-meye-cyan/5 p-3 space-y-2" data-testid="task-edit-split-preview">
                            <div className="text-[10px] uppercase tracking-widest text-meye-cyan">proposed split · nothing saved yet</div>
                            <div className="text-sm font-semibold text-zinc-100" data-testid="task-edit-split-title">{splitPreview.title}</div>
                            <div className="text-xs text-zinc-400 whitespace-pre-wrap max-h-28 overflow-y-auto" data-testid="task-edit-split-body">{splitPreview.description}</div>
                            <div className="flex gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            const res = await api.applyTaskSplits([{ id: task.id, title: splitPreview.title, description: splitPreview.description }]);
                                            const doc = res?.tasks?.[0];
                                            if (doc) { setText(doc.text || ""); setDescription(doc.description || ""); onTaskUpdated?.(doc); }
                                            setSplitPreview(null);
                                            toast.success("Split applied — original text kept in history");
                                        } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Split failed"); }
                                    }}
                                    className="px-3 py-1.5 rounded bg-meye-cyan text-black text-xs font-brand font-bold uppercase tracking-widest"
                                    data-testid="task-edit-split-apply"
                                >
                                    apply split
                                </button>
                                <button type="button" onClick={() => setSplitPreview(null)} className="px-3 py-1.5 rounded border border-meye-border text-xs text-zinc-400 uppercase tracking-widest" data-testid="task-edit-split-cancel">cancel</button>
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-[10px] uppercase tracking-widest text-zinc-500">body / details</label>
                            {task.split_from_text && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            const doc = await api.unsplitTask(task.id);
                                            setText(doc.text || ""); setDescription(doc.description || ""); onTaskUpdated?.(doc);
                                            toast.success("Original combined text restored");
                                        } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Restore failed"); }
                                    }}
                                    className="text-[10px] font-brand uppercase tracking-widest text-zinc-500 hover:text-amber-300"
                                    data-testid="task-edit-unsplit-btn"
                                >
                                    restore original text
                                </button>
                            )}
                        </div>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={description.trim() ? 5 : 2}
                            placeholder="optional details — shown under the title, never truncated here"
                            className="w-full bg-black/40 border border-meye-border rounded p-2.5 text-sm text-zinc-300 font-mono focus:outline-none focus:border-meye-cyan/60"
                            data-testid="task-edit-description"
                        />
                        {(() => {
                            // Read-only Links area — the textarea stays plain text; detected URLs open in a new tab.
                            const links = extractUrls(`${text}\n${description}`);
                            if (!links.length) return null;
                            return (
                                <div className="mt-2 rounded border border-meye-cyan/30 bg-meye-cyan/5 px-3 py-2" data-testid="task-edit-links">
                                    <div className="text-[10px] uppercase tracking-widest text-meye-cyan mb-1">links · {links.length}</div>
                                    <ul className="space-y-0.5">
                                        {links.map((l) => (
                                            <li key={l.href} className="min-w-0">
                                                <a href={l.href} target="_blank" rel="noopener noreferrer" className="text-xs text-meye-cyan underline underline-offset-2 break-all" data-testid="task-edit-link" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>{l.label}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })()}
                    </div>

                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="task-edit-date-label">
                                {task.type === "recurring" && task.interval ? "occurrence date" : "date"}
                            </label>
                            <DatePicker value={date} onChange={setDate} testid="task-edit-date" />
                        </div>
                        <div className="flex-1">
                            <label className="text-[10px] uppercase tracking-widest text-zinc-500">time</label>
                            <TimePicker value={time} onChange={setTime} testid="task-edit-time" />
                        </div>
                    </div>

                    {task.type === "recurring" && task.interval && task.date && (
                        <div className="-mt-2 flex items-start gap-3 rounded border border-dashed border-meye-border/70 bg-black/20 px-3 py-2" data-testid="task-edit-series-start">
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500">series start · read-only</div>
                                <div className="text-sm font-mono text-zinc-300" data-testid="task-edit-series-anchor">{formatDate(task.date)}</div>
                            </div>
                            <div className="flex-1 min-w-0 text-[10px] font-mono text-zinc-500 leading-snug">
                                the date field above is the <span className="text-zinc-300">occurrence</span> you're editing
                                {date && date !== resolveOccurrenceIso(task) ? (
                                    <> — saving moves only <span className="text-meye-cyan">{formatDate(resolveOccurrenceIso(task))} → {formatDate(date)}</span>, the series stays anchored on {formatDate(task.date)} unless you pick "entire series"</>
                                ) : (
                                    <> — pick a new date to move just this beat; the series anchor is never changed unless you choose "entire series"</>
                                )}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500">type</label>
                        <div className="flex gap-2 mt-1">
                            {["todo", "scheduled", "recurring"].map((tp) => (
                                <button
                                    key={tp}
                                    onClick={() => setType(tp)}
                                    className={`text-xs px-3 py-1.5 rounded border uppercase tracking-widest font-mono transition-colors ${type === tp ? "border-meye-cyan text-meye-cyan bg-meye-cyan/10" : "border-meye-border text-zinc-400 hover:text-zinc-200"}`}
                                    data-testid={`task-type-${tp}`}
                                >
                                    {tp}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                            {(() => {
                                const cls = priority === "high" ? "text-red-400" : priority === "low" ? "text-meye-fuchsia" : "text-meye-cyan";
                                return <Flag size={10} className={cls} />;
                            })()}
                            priority
                        </label>
                        <div className="flex gap-2 mt-1">
                            {[
                                { v: "high", l: "high",   iconCls: "text-red-400",       ring: "border-red-400 text-red-300 bg-red-500/10" },
                                { v: "med",  l: "medium", iconCls: "text-meye-cyan",     ring: "border-meye-cyan text-meye-cyan bg-meye-cyan/10" },
                                { v: "low",  l: "low",    iconCls: "text-meye-fuchsia",  ring: "border-meye-fuchsia text-meye-fuchsia bg-meye-fuchsia/10" },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => setPriority(o.v)}
                                    className={`text-xs px-3 py-1.5 rounded border uppercase tracking-widest font-mono transition-colors inline-flex items-center gap-1.5 ${priority === o.v ? o.ring : "border-meye-border text-zinc-400 hover:text-zinc-200"}`}
                                    data-testid={`task-priority-${o.v}`}
                                >
                                    <Flag size={11} strokeWidth={2.25} className={priority === o.v ? o.iconCls : "text-zinc-500"} />
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {type === "recurring" && (
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-zinc-500">every (days)</label>
                            <div className="flex gap-2 mt-1">
                                {[
                                    { l: "daily", v: 1 },
                                    { l: "weekly", v: 7 },
                                    { l: "monthly", v: 30 },
                                    { l: "yearly", v: 365 },
                                ].map((o) => (
                                    <button
                                        key={o.v}
                                        onClick={() => setIntervalVal(o.v)}
                                        className={`text-xs px-2.5 py-1.5 rounded border uppercase tracking-widest font-mono transition-colors ${interval === o.v ? "border-meye-fuchsia text-meye-fuchsia bg-meye-fuchsia/10" : "border-meye-border text-zinc-400"}`}
                                    >
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                            <Bell size={10} /> remind me
                        </label>
                        <div className="flex gap-2 mt-1 flex-wrap">
                            {REMINDER_PRESETS.map((p) => (
                                <button
                                    key={p.v}
                                    onClick={() => toggleReminder(p.v)}
                                    className={`text-xs px-2.5 py-1 rounded border font-mono transition-colors ${remindersMin.includes(p.v) ? "border-meye-fuchsia text-meye-fuchsia bg-meye-fuchsia/10" : "border-meye-border text-zinc-500 hover:text-zinc-200"}`}
                                    data-testid={`reminder-${p.v}`}
                                >
                                    {p.l} before
                                </button>
                            ))}
                        </div>
                        {/* Custom reminder builder */}
                        <div className="flex items-center gap-2 mt-2">
                            <input
                                type="number"
                                min="1"
                                value={customVal}
                                onChange={(e) => setCustomVal(e.target.value)}
                                placeholder="custom"
                                className="w-20 bg-black/40 border border-meye-border rounded px-2 py-1 text-xs text-zinc-100 focus:outline-none focus:border-meye-cyan/60"
                                data-testid="reminder-custom-val"
                            />
                            <select
                                value={customUnit}
                                onChange={(e) => setCustomUnit(e.target.value)}
                                className="bg-black/40 border border-meye-border rounded px-2 py-1 text-xs text-zinc-100 focus:outline-none focus:border-meye-cyan/60"
                                data-testid="reminder-custom-unit"
                            >
                                <option value="min">min</option>
                                <option value="hr">hr</option>
                                <option value="day">day</option>
                                <option value="wk">wk</option>
                            </select>
                            <button
                                type="button"
                                onClick={addCustomReminder}
                                className="text-[10px] font-brand uppercase tracking-widest px-2 py-1 rounded border border-meye-cyan/60 text-meye-cyan hover:bg-meye-cyan/10 transition-colors"
                                data-testid="reminder-custom-add"
                            >
                                add
                            </button>
                        </div>
                        {/* Chip list of active reminders (mostly needed to expose non-preset custom ones for removal) */}
                        {remindersMin.filter((m) => !REMINDER_PRESETS.find((p) => p.v === m)).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {remindersMin
                                    .filter((m) => !REMINDER_PRESETS.find((p) => p.v === m))
                                    .map((m) => (
                                        <button
                                            key={m}
                                            onClick={() => toggleReminder(m)}
                                            className="text-[10px] font-mono px-2 py-1 rounded-full border border-meye-fuchsia/60 text-meye-fuchsia bg-meye-fuchsia/10 hover:bg-meye-fuchsia/20 transition-colors inline-flex items-center gap-1"
                                            title="Click to remove"
                                            data-testid={`reminder-custom-chip-${m}`}
                                        >
                                            {formatMinutes(m)} before
                                            <X size={10} />
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                            <Zap size={10} /> nag every
                        </label>
                        {time ? (
                            <div className="flex gap-2 mt-1">
                                {NAG_PRESETS.map((p) => (
                                    <button
                                        key={p.v}
                                        onClick={() => setNagEveryMin(p.v)}
                                        className={`text-xs px-2.5 py-1 rounded border font-mono transition-colors ${nagEveryMin === p.v ? "border-meye-cyan text-meye-cyan bg-meye-cyan/10" : "border-meye-border text-zinc-500 hover:text-zinc-200"}`}
                                        data-testid={`nag-every-${p.v}`}
                                    >
                                        {p.l}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            // No time → the nag scheduler has no scheduled instant to fire against,
                            // so the presets would be meaningless (and misleading if one looked "lit").
                            <p className="text-[11px] text-zinc-600 mt-1 italic" data-testid="nag-every-disabled-hint">
                                Add a time to enable nagging.
                            </p>
                        )}
                    </div>

                    {atts.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                                    <Paperclip size={10} /> attachments · {atts.length}
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setEditingAtts((v) => !v)}
                                    className={`text-[10px] font-brand uppercase tracking-widest px-2 py-1 rounded border transition-colors flex items-center gap-1 ${
                                        editingAtts
                                            ? "border-meye-fuchsia/70 text-meye-fuchsia bg-meye-fuchsia/10"
                                            : "border-meye-border text-zinc-500 hover:text-meye-cyan hover:border-meye-cyan/40"
                                    }`}
                                    data-testid="task-attachments-edit-toggle"
                                >
                                    {editingAtts ? <Check size={10} /> : <Edit3 size={10} />}
                                    {editingAtts ? "done" : "edit"}
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {atts.map((a) => {
                                    const isImage = a.type === "image";
                                    const isPdf =
                                        a.type === "pdf" ||
                                        (a.data || "").startsWith("data:application/pdf") ||
                                        (a.name || "").toLowerCase().endsWith(".pdf");
                                    return (
                                        <div key={a.id} className="relative group">
                                            {isImage ? (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPreview(a);
                                                    }}
                                                    className="rounded border border-meye-border hover:border-meye-cyan/60 transition-colors overflow-hidden block"
                                                    title={`Preview ${a.name}`}
                                                    data-testid={`task-attachment-thumb-${a.id}`}
                                                >
                                                    <img
                                                        src={a.data}
                                                        alt={a.name}
                                                        className="w-16 h-16 object-cover"
                                                    />
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPreview(a);
                                                    }}
                                                    className="flex items-center gap-1.5 text-xs text-zinc-300 border border-meye-border rounded pl-2 pr-6 py-1.5 hover:border-meye-cyan/60 hover:text-meye-cyan transition-colors max-w-[220px]"
                                                    title={`Preview ${a.name}`}
                                                    data-testid={`task-attachment-thumb-${a.id}`}
                                                >
                                                    {isPdf ? <FileText size={13} /> : <FileIcon size={13} />}
                                                    <span className="truncate">{a.name || (isPdf ? "document.pdf" : "attachment")}</span>
                                                </button>
                                            )}
                                            {editingAtts && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => deleteAttachment(a, e)}
                                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500/90 text-black flex items-center justify-center opacity-90 hover:opacity-100 hover:bg-red-400 shadow-md transition-opacity"
                                                    aria-label={`Delete attachment ${a.name}`}
                                                    title="Delete attachment"
                                                    data-testid={`task-attachment-delete-${a.id}`}
                                                >
                                                    <X size={11} strokeWidth={3} />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {isRecurringSeries && (
                        <OccurrenceHistoryList task={task} onTaskUpdated={onTaskUpdated} />
                    )}
                </div>

                {/* Pinned footer: recurring scope choice + actions. Never scrolls out of reach. */}
                <div className="flex-shrink-0 border-t border-meye-border bg-meye-card px-4 py-3 space-y-3" data-testid="task-editor-footer">
                    {needsScopePrompt() && (
                        <div
                            className="rounded-md border border-meye-cyan/40 bg-meye-cyan/5 p-2"
                            data-testid="recurring-scope-selector"
                        >
                            <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 font-mono">
                                apply change to
                            </div>
                            <div className="text-[10px] font-mono text-zinc-500 mb-1.5" data-testid="recurring-scope-hint">
                                {scope === "occurrence"
                                    ? `moves only the ${formatDate(resolveOccurrenceIso(task))} beat${date && date !== resolveOccurrenceIso(task) ? ` → ${formatDate(date)}` : ""} — series stays anchored on ${formatDate(task.date)}`
                                    : `re-anchors the whole series: start date becomes ${formatDate(date || task.date)} and every future beat shifts`}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setScope("occurrence")}
                                    className={`text-xs px-3 py-2 rounded border uppercase tracking-widest font-brand font-bold transition-colors ${
                                        scope === "occurrence"
                                            ? "border-meye-cyan text-meye-cyan bg-meye-cyan/10"
                                            : "border-meye-border text-zinc-400 hover:text-zinc-200"
                                    }`}
                                    data-testid="scope-occurrence"
                                >
                                    just this occurrence
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setScope("series")}
                                    className={`text-xs px-3 py-2 rounded border uppercase tracking-widest font-brand font-bold transition-colors ${
                                        scope === "series"
                                            ? "border-meye-fuchsia text-meye-fuchsia bg-meye-fuchsia/10"
                                            : "border-meye-border text-zinc-400 hover:text-zinc-200"
                                    }`}
                                    data-testid="scope-series"
                                >
                                    entire series
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                            {!confirmingDelete && (
                                <button
                                    onClick={() => {
                                        // Recurring tasks demand explicit confirmation — a delete here
                                        // wipes every future occurrence. For a single-day skip the user
                                        // should tap DONE (which check-ins the current cycle) instead.
                                        if (task.type === "recurring") {
                                            setConfirmingDelete(true);
                                            return;
                                        }
                                        onDelete(task.id);
                                        onOpenChange(false);
                                    }}
                                    className="text-xs font-brand font-bold uppercase tracking-widest px-3 py-2 rounded bg-red-500/90 hover:bg-red-500 text-white flex items-center gap-1.5 transition-colors"
                                    data-testid="task-editor-delete"
                                >
                                    <Trash2 size={13} strokeWidth={2.5} /> delete
                                </button>
                            )}
                            <button
                                onClick={() => fileRef.current?.click()}
                                className="text-xs text-zinc-500 hover:text-meye-cyan flex items-center gap-1"
                                data-testid="task-editor-attach"
                            >
                                <Paperclip size={13} /> attach
                            </button>
                        </div>
                        {confirmingDelete && (
                            <div
                                className="w-full flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2"
                                data-testid="task-editor-delete-confirm"
                            >
                                <div className="text-[11px] text-zinc-200 leading-snug max-w-[26rem]">
                                    <span className="font-brand font-bold uppercase tracking-widest text-red-300">Delete entire series?</span>
                                    <br />
                                    <span className="text-zinc-400">
                                        This wipes every future occurrence. To skip just today, tap <span className="text-emerald-400 font-bold">DONE</span> — it check-ins this cycle and leaves the schedule alone.
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setConfirmingDelete(false)}
                                        className="text-xs font-brand font-bold uppercase tracking-widest px-3 py-2 rounded border border-meye-border bg-black/40 text-zinc-300 hover:text-white"
                                        data-testid="task-editor-delete-cancel"
                                    >
                                        cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onDelete(task.id);
                                            onOpenChange(false);
                                        }}
                                        className="text-xs font-brand font-bold uppercase tracking-widest px-3 py-2 rounded border border-red-400/70 bg-red-500/80 text-white hover:bg-red-500"
                                        data-testid="task-editor-delete-confirm-btn"
                                    >
                                        delete series
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            {onComplete && (
                                <label
                                    className="flex items-center gap-2 cursor-pointer select-none group"
                                    data-testid="task-editor-complete-toggle"
                                >
                                    <input
                                        type="checkbox"
                                        checked={isRecurringSeries ? occDone : !!task.completed}
                                        onChange={async () => {
                                            // Persist any pending field edits first so toggling complete never loses text/date changes.
                                            if (isDirty()) await save({ silent: true });
                                            if (isRecurringSeries && occDone) {
                                                // Reopen exactly the selected SOURCE occurrence (atomic $pull) — never the series.
                                                try {
                                                    const traceId = api.newTraceId();
                                                    logDiag({ kind: "complete.click", task_id: task.id, iso: selectedSourceIso, view: "editor", trace_id: traceId, message: `"${String(task.text || "").slice(0, 60)}" reopen`, state: stateSummary(task) });
                                                    const doc = await api.reopenOccurrence(task.id, selectedSourceIso, traceId);
                                                    logDiag({ kind: "complete.response", task_id: task.id, iso: selectedSourceIso, trace_id: traceId, state: stateSummary(doc) });
                                                    onTaskUpdated?.(doc);
                                                    toast.success(`Reopened ${formatDate(selectedSourceIso)}`);
                                                } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't reopen"); }
                                                handleOpenChange(false);
                                                return;
                                            }
                                            onComplete(task, isRecurringSeries ? selectedSourceIso : null);
                                            handleOpenChange(false);
                                        }}
                                        className="w-5 h-5 rounded border-2 border-meye-cyan/60 bg-transparent accent-emerald-500 cursor-pointer"
                                        data-testid="task-editor-complete-checkbox"
                                    />
                                    <span className={`text-xs font-brand font-bold uppercase tracking-widest transition-colors ${(isRecurringSeries ? occDone : task.completed) ? "text-emerald-400" : "text-zinc-400 group-hover:text-emerald-400"}`}>
                                        {(isRecurringSeries ? occDone : task.completed) ? "completed" : "done"}
                                    </span>
                                </label>
                            )}
                            <button
                                type="button"
                                onClick={async () => {
                                    const ok = await save();
                                    if (ok) onOpenChange(false);
                                }}
                                className="text-xs bg-meye-cyan hover:bg-cyan-300 text-black font-brand font-bold uppercase tracking-widest px-4 py-2 rounded flex items-center gap-1.5 transition-colors"
                                data-testid="task-editor-save"
                            >
                                <Save size={13} /> save
                            </button>
                        </div>
                    </div>
                </div>
                <MediaLightbox attachment={preview} onClose={() => setPreview(null)} />
            </DialogContent>
        </Dialog>
    );
};

export default TaskEditor;
